import React, { useState, useEffect } from 'react';
import fuelTrackingService, { TrackingSessionResponse } from '../services/fuelTrackingService';
import carService from '../services/carService';
import { Car } from '../services/carService';
import { ChevronRightIcon, FuelIcon, ClockIcon, CarIcon } from './icons';
import './HistoryPage.css';

interface HistoryPageProps {
  currentUser?: {
    id: number;
    email: string;
    username: string;
    name: string;
    surname: string;
    role: string;
  } | null;
}

const HistoryPage: React.FC<HistoryPageProps> = ({ currentUser }) => {
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [sessions, setSessions] = useState<TrackingSessionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all');

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('HistoryPage - Fetching cars...');
      const response = await carService.getCars();
      console.log('HistoryPage - Cars response:', response);
      
      setCars(response.content || response);
    } catch (err) {
      console.error('Error fetching cars:', err);
      setError(`Ошибка при загрузке машин: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarSessions = async (carId: number) => {
    try {
      setSessionsLoading(true);
      setError(null);
      
      console.log('HistoryPage - Fetching sessions for carId:', carId);
      const response = await fuelTrackingService.getSessionsByCar(carId);
      console.log('HistoryPage - Sessions response:', response);
      
      // Убеждаемся, что response - это массив
      const sessionsArray = Array.isArray(response) ? response : [];
      console.log('HistoryPage - Sessions array:', sessionsArray);
      
      setSessions(sessionsArray);
    } catch (err) {
      console.error('Error fetching car sessions:', err);
      setError(`Ошибка при загрузке сессий машины: ${err instanceof Error ? err.message : 'Неизвестная ошибка'}`);
      setSessions([]); // Устанавливаем пустой массив при ошибке
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleCarClick = async (car: Car) => {
    setSelectedCar(car);
    await fetchCarSessions(car.id);
  };

  const handleBackToCars = () => {
    setSelectedCar(null);
    setSessions([]);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Активна';
      case 'COMPLETED':
        return 'Завершена';
      case 'CANCELLED':
        return 'Отменена';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return '#10b981';
      case 'COMPLETED':
        return '#3b82f6';
      case 'CANCELLED':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const formatLiters = (liters: number | null | undefined) => {
    if (liters === null || liters === undefined || isNaN(liters)) {
      return '—';
    }
    return `${liters.toFixed(2)} л`;
  };

  const filteredCars = cars.filter(car => {
    const matchesSearch = car.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         car.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         car.number.toString().includes(searchTerm);
    
    return matchesSearch;
  });

  const filteredSessions = sessions.filter(session => {
    const matchesStatus = !selectedStatus || session.status === selectedStatus;
    
    let matchesDateRange = true;
    if (selectedDateRange !== 'all') {
      const sessionDate = new Date(session.startTime);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (selectedDateRange) {
        case 'today':
          const sessionToday = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
          matchesDateRange = sessionToday.getTime() === today.getTime();
          break;
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          matchesDateRange = sessionDate >= weekAgo;
          break;
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          matchesDateRange = sessionDate >= monthAgo;
          break;
      }
    }
    
    return matchesStatus && matchesDateRange;
  });

  if (selectedCar) {
    return (
      <div className="history-page">
        <div className="history-header">
          <button onClick={handleBackToCars} className="back-button">
            ← Назад к машинам
          </button>
          <h1>История сессий</h1>
        </div>

        <div className="selected-car-info">
          <div className="car-avatar">
            <CarIcon className="car-icon" size={24} />
          </div>
          <div className="car-details">
            <h2>{selectedCar.name} {selectedCar.model}</h2>
            <p className="car-number">№{selectedCar.number}</p>
            <div className="car-meta">
              <span className="car-type-badge">Топливозаправщик</span>
              <span className={`car-status-badge ${selectedCar.isActive ? 'active' : 'inactive'}`}>
                {selectedCar.isActive ? 'Активна' : 'Неактивна'}
              </span>
            </div>
          </div>
        </div>

        <div className="sessions-filters">
          <div className="filter-container">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">Все статусы</option>
              <option value="ACTIVE">Активные</option>
              <option value="COMPLETED">Завершенные</option>
              <option value="CANCELLED">Отмененные</option>
            </select>
            
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="filter-select"
            >
              <option value="all">Все время</option>
              <option value="today">Сегодня</option>
              <option value="week">За неделю</option>
              <option value="month">За месяц</option>
            </select>
          </div>
        </div>

        {sessionsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Загрузка сессий...</p>
          </div>
        ) : filteredSessions.length > 0 ? (
          <div className="sessions-list">
            <h3>Сессии отслеживания ({filteredSessions.length})</h3>
            <div className="sessions-grid">
              {filteredSessions.map((session) => (
                <div key={session.id} className="session-card">
                  <div className="session-header">
                    <div className="session-id">
                      <span className="session-number">#{session.id}</span>
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(session.status) }}
                      >
                        {getStatusText(session.status)}
                      </span>
                    </div>
                    <div className="session-time">
                      <ClockIcon className="time-icon" size={16} />
                      <span>{session.formattedDuration}</span>
                    </div>
                  </div>
                  
                  <div className="session-details">
                    <div className="detail-row">
                      <span className="detail-label">Начало:</span>
                      <span className="detail-value">{formatDateTime(session.startTime)}</span>
                    </div>
                    {session.endTime && (
                      <div className="detail-row">
                        <span className="detail-label">Конец:</span>
                        <span className="detail-value">{formatDateTime(session.endTime)}</span>
                      </div>
                    )}
                    
                    {session.fuelConsumption !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Расход:</span>
                        <span className="detail-value fuel-consumption">
                          {formatLiters(session.fuelConsumption)}
                        </span>
                      </div>
                    )}
                    
                    {session.initialFuelLevel !== null && session.finalFuelLevel !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Уровни:</span>
                        <span className="detail-value fuel-levels">
                          {formatLiters(session.initialFuelLevel)} → {formatLiters(session.finalFuelLevel)}
                        </span>
                      </div>
                    )}
                    
                    {session.fuelRefill !== null && session.fuelRefill > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Пополнение:</span>
                        <span className="detail-value fuel-refill">
                          +{formatLiters(session.fuelRefill)}
                        </span>
                      </div>
                    )}
                    
                    {session.netChange !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Изменение:</span>
                        <span className={`detail-value net-change ${session.netChange >= 0 ? 'positive' : 'negative'}`}>
                          {session.netChange >= 0 ? '+' : ''}{formatLiters(session.netChange)}
                        </span>
                      </div>
                    )}
                    
                    {session.totalSensors !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Датчиков:</span>
                        <span className="detail-value">{session.totalSensors}</span>
                      </div>
                    )}
                    
                    {session.dataPointsCount !== null && (
                      <div className="detail-row">
                        <span className="detail-label">Точек данных:</span>
                        <span className="detail-value">{session.dataPointsCount}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <FuelIcon className="empty-icon" size={48} />
            <p>У машины нет сессий отслеживания</p>
            <p className="empty-subtitle">Сессии появятся после начала отслеживания топлива</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-header">
        <h1>История сессий отслеживания</h1>
        <p>Просмотр истории сессий отслеживания топлива по машинам</p>
      </div>

      <div className="history-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Поиск по названию, модели или номеру машины..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка машин...</p>
        </div>
      ) : filteredCars.length > 0 ? (
        <div className="cars-grid">
          {filteredCars.map((car) => (
            <div 
              key={car.id} 
              className="car-card"
              onClick={() => handleCarClick(car)}
            >
              <div className="car-avatar">
                <CarIcon className="car-icon" size={24} />
              </div>
              
              <div className="car-info">
                <h3 className="car-name">{car.name} {car.model}</h3>
                <p className="car-number">№{car.number}</p>
                
                <div className="car-badges">
                  <span className="car-type-badge">Топливозаправщик</span>
                  <span className={`car-status-badge ${car.isActive ? 'active' : 'inactive'}`}>
                    {car.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                </div>
              </div>
              
              <div className="car-actionss">
                <ChevronRightIcon className="chevron-icon" size={20} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <CarIcon className="empty-icon" size={48} />
          <p>Машины не найдены</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
